import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, makeStyles } from '@material-ui/core';
import { ArticleTypeExternal, Currency, formatDateTimeReadable } from '@nemuru-eng/js-api-client';
import { Fragment, useState } from 'react';

import { DialogHeader } from '@/components/shared/components/ItemDetail/components/shared/DialogHeader';
import { ItemDetailProps } from '@/components/shared/components/ItemDetail/types/ItemDetailProps';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { CustomDivider } from '@/components/shared/components/CustomDivider';
import { Font } from '@/components/shared/components/Font';
import { ReadOnlyDataRow } from '@/components/shared/components/ReadOnlyDataRow';
import { Text } from '@/components/shared/components/Text';

export const Flight = ({ itemDetail, itemStatus, currency }: ItemDetailProps) => {
  if (itemDetail.article.type !== ArticleTypeExternal.FLIGHT) {
    return null;
  }

  const [expanded, setExpanded] = useState(false);
  const toggleExpanded = () => {
    setExpanded(!expanded);
  };
  const useStyles = makeStyles({
    root: {
      '&:before': {
        display: 'none',
      },
      '& .MuiAccordionSummary-root': { padding: 0 },
    },
    details: { '&.MuiAccordionDetails-root': { padding: 0 } },
    summary: {
      '& .MuiAccordionSummary-content': {
        padding: 0,
      },
    },
  });
  const passengers = itemDetail.article.passengers;
  const segments = itemDetail.article.segments;

  const classes = useStyles();
  return (
    <>
      <DialogHeader
        articleType={ArticleTypeExternal.FLIGHT}
        articleDetailId={itemDetail.id}
        total={{ value: itemDetail.totalPriceWithTax, currency: currency ?? Currency.EUR }}
      />
      {itemStatus && (
        <Box pb={2}>
          <StatusTag type={itemStatus.statusColor} labelTextId={itemStatus.status} />
        </Box>
      )}
      <Box py={2}>
        <ReadOnlyDataRow labelTextId={'REFERENCE'} value={itemDetail.article.reference ?? '-'} underlined />
        <ReadOnlyDataRow labelTextId={'NAME'} value={itemDetail.article.name} underlined />
      </Box>
      <Box py={2}>
        <Box pb={2}>
          <Font variant={'body1'} color={'title'}>
            <Text strong tId={'PASSENGERS'} dynamicDataTexts={[`${passengers.length}`]} />
          </Font>
        </Box>
        {passengers.map((passenger, i) => (
          <ReadOnlyDataRow
            key={i}
            labelTextId={`PASSENGER_${i + 1}`}
            value={`${passenger.firstName} ${passenger.lastName}`}
            underlined
          />
        ))}
      </Box>
      {segments && (
        <Box py={2}>
          <Box pb={2}>
            <Font variant={'body1'} color={'title'}>
              <Text strong tId={'SEGMENTS'} dynamicDataTexts={[`${passengers.length}`]} />
            </Font>
          </Box>
          {segments.map((segment, i) => (
            <Fragment key={i}>
              <Accordion
                className={classes.root}
                elevation={0}
                style={{ paddingInline: 0 }}
                expanded={expanded}
                onChange={toggleExpanded}
              >
                <AccordionSummary className={classes.summary}>
                  <Box width={'100%'}>
                    <Box pt={1} display={'flex'} justifyContent={'space-between'}>
                      <Font variant="body2" color={'textPrimary'}>
                        <strong>{segment.iataDepartureCode}</strong>
                      </Font>
                      {segment.departureTime && (
                        <Font variant="body2" color={'textSecondary'} align="right">
                          {formatDateTimeReadable(segment.departureTime)}
                        </Font>
                      )}
                    </Box>
                    <Box pt={1} display={'flex'} justifyContent={'space-between'}>
                      <Font variant="body2" color={'textPrimary'}>
                        <strong>{segment.iataDestinationCode}</strong>
                      </Font>
                      {segment.destinationTime && (
                        <Font variant="body2" color={'textSecondary'} align="right">
                          {formatDateTimeReadable(segment.destinationTime)}
                        </Font>
                      )}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails className={classes.details}>
                  <Grid container>
                    <Grid item xs={12}>
                      <ReadOnlyDataRow labelTextId={'CARRIER_CODE'} value={segment.iataCarrierCode ?? '-'} underlined />
                      <ReadOnlyDataRow labelTextId={'FLIGHT_CODE'} value={segment.iataFlightCode ?? '-'} underlined />
                      <ReadOnlyDataRow labelTextId={'CABIN_CLASS'} value={segment.cabinClass ?? '-'} underlined />
                      <ReadOnlyDataRow labelTextId={'FARE_BASIS'} value={segment.fareBasis ?? '-'} underlined />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
              {!expanded && <CustomDivider orientation={'horizontal'} margin={1} />}
            </Fragment>
          ))}
        </Box>
      )}
    </>
  );
};
